import { useCallback, useEffect, useRef, useState } from "react";
import { Send, Loader2, Camera, X, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, errMsg, fileUrl, uploadFile } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const time = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export function ChatThread({ technicianId, jobId, className = "h-[420px]", onSent }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [pending, setPending] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const fileRef = useRef(null);
  const params = { technician_id: technicianId || undefined, job_id: jobId || undefined };

  const load = useCallback(() => api.get("/chat/messages", { params }).then((r) => setMsgs(r.data.messages)).catch(() => {}),
    [technicianId, jobId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setMsgs(null);
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs?.length]);

  const pick = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - pending.length);
    e.target.value = "";
    setUploading(true);
    try {
      for (const f of files) {
        const res = await uploadFile("/chat/upload", f);
        setPending((p) => [...p, { ...res, preview: URL.createObjectURL(f) }]);
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const removeAtt = async (m, a) => {
    if (!window.confirm("Eliminare questa foto per tutti? Non sarà più visibile né a te né a chi l'ha ricevuta.")) return;
    try {
      const { data } = await api.delete(`/chat/messages/${m.id}/attachments/${a.file_id}`);
      setMsgs((list) => list.map((x) => (x.id === m.id ? data : x)));
      toast.success("Allegato eliminato");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const send = async () => {
    if (!text.trim() && !pending.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/chat/messages", { ...params, text, attachment_ids: pending.map((p) => p.file_id) });
      setMsgs((m) => [...(m || []), data]);
      setText("");
      setPending([]);
      onSent?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`flex flex-col ${className}`} data-testid="chat-thread">
      <div className="flex-1 space-y-2 overflow-y-auto rounded-xl bg-slate-50 p-3" data-testid="chat-messages">
        {msgs === null ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          : !msgs.length ? <p className="py-8 text-center text-xs text-slate-500" data-testid="chat-empty">Nessun messaggio. Scrivi il primo.</p>
          : msgs.map((m, i) => {
            const mine = m.from_id === user?.id;
            const canDel = mine || user?.role === "admin";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${i}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "rounded-br-sm bg-[#61a229] text-white" : "rounded-bl-sm border bg-white text-slate-800"}`}>
                  {!mine && <p className="text-[10px] font-bold text-[#61a229]">{m.from_role === "admin" ? `Ufficio · ${m.from_name}` : m.from_name}</p>}
                  {m.attachments?.length > 0 && (
                    <div className={`mb-1 mt-1 grid gap-1 ${m.attachments.length > 1 ? "grid-cols-2" : "grid-cols-1"}`} data-testid={`chat-message-${i}-photos`}>
                      {m.attachments.map((a, j) => (
                        <div key={a.file_id} className="relative">
                          <a href={fileUrl(a.file_id)} target="_blank" rel="noreferrer">
                            <img src={fileUrl(a.file_id)} alt={a.name} className="max-h-48 w-full rounded-lg object-cover" />
                          </a>
                          {canDel && <button type="button" onClick={() => removeAtt(m, a)} title="Elimina per tutti" data-testid={`chat-message-${i}-delete-photo-${j}`}
                            className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white transition-colors hover:bg-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
                        </div>
                      ))}
                    </div>
                  )}
                  {m.removed_attachments > 0 && <p className={`text-xs italic ${mine ? "text-white/80" : "text-slate-400"}`} data-testid={`chat-message-${i}-removed`}>
                    {m.removed_attachments === 1 ? "Allegato eliminato" : `${m.removed_attachments} allegati eliminati`}</p>}
                  {m.text && <p className="whitespace-pre-line break-words">{m.text}</p>}
                  <p className={`mt-0.5 text-[10px] ${mine ? "text-white/70" : "text-slate-400"}`}>{m.broadcast ? "Messaggio di gruppo · " : ""}{time(m.created_at)}</p>
                </div>
              </div>
            );
          })}
        <div ref={end} />
      </div>
      {pending.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="chat-pending-photos">
          {pending.map((p, i) => (
            <div key={p.file_id} className="relative h-14 w-14 overflow-hidden rounded-lg border">
              <img src={p.preview} alt="" className="h-full w-full object-cover" />
              <button onClick={() => setPending((x) => x.filter((y) => y.file_id !== p.file_id))} data-testid={`chat-pending-remove-${i}`}
                className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white"><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}
      <div className="mt-2 flex items-end gap-2">
        <Button type="button" variant="outline" size="icon" onClick={() => fileRef.current?.click()} disabled={uploading || pending.length >= 6}
          className="h-11 w-11 shrink-0 rounded-full" data-testid="chat-photo-button">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
        </Button>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={pick} data-testid="chat-photo-input" />
        <Textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi un messaggio…" className="min-h-[44px] resize-none"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} data-testid="chat-input" />
        <Button onClick={send} disabled={busy || uploading || (!text.trim() && !pending.length)} size="icon" className="h-11 w-11 shrink-0 rounded-full" data-testid="chat-send-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

export function ThreadList({ threads, selected, onSelect, showTech }) {
  return (
    <div className="divide-y" data-testid="chat-thread-list">
      {threads.map((t, i) => {
        const key = `${t.technician_id}:${t.job_id || ""}`;
        return (
          <button key={key} onClick={() => onSelect(t)} data-testid={`chat-thread-item-${i}`}
            className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 ${selected === key ? "bg-[#a2ca6b]/15" : ""}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{showTech ? t.technician_name : t.job_id ? "Intervento" : "Ufficio"}
                {t.job_label && <span className="ml-1 font-normal text-slate-500">· {t.job_label}</span>}</p>
              <p className="truncate text-xs text-slate-500">{t.last ? `${t.last.from_name}: ${t.last.text || "Foto"}` : "Nessun messaggio"}</p>
            </div>
            {t.unread > 0 && <span className="rounded-full bg-[#61a229] px-2 py-0.5 text-[10px] font-bold text-white" data-testid={`chat-thread-unread-${i}`}>{t.unread}</span>}
          </button>
        );
      })}
      {!threads.length && <p className="px-4 py-8 text-center text-sm text-slate-500">Nessuna conversazione.</p>}
    </div>
  );
}

export function useUnread(enabled = true) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const f = () => api.get("/chat/unread").then((r) => setN(r.data.unread)).catch(() => {});
    f();
    const t = setInterval(f, 20000);
    return () => clearInterval(t);
  }, [enabled]);
  return n;
}
